import { components, paths } from "@/api-schema";
import {
  PathsV1BrandSearchPostParametersQueryExtend,
  PathsV1BrandSearchPostParametersQueryOrderByDirection,
  PathsV1BrandSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useBrandDeleteMutation } from "@/common/api/brand/use-brand-delete.mutation";
import { useBrandRestoreMutation } from "@/common/api/brand/use-brand-restore.mutation";
import { useBrandSearchQuery } from "@/common/api/brand/use-brand-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures, TimestampSortField } from "@/components/data-table/types";
import { BrandDatIdFilter } from "@/components/filter/predefined/brand-dat-id-filter";
import { BrandTypeFilter } from "@/components/filter/predefined/brand-type-filter";
import { DeletedStatus, DeletedStatusFilter } from "@/components/filter/predefined/deleted-status-filter";
import { RowActionVariant } from "@/components/ui/table-row-actions";
import i18next from "i18next";
import { GitMerge } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { brandTableDefaultColumns } from "./brand-table-columns";

type Brand = components["schemas"]["Brand"] & RowWithIdentifier;
type BrandSorting = NonNullable<paths["/v1/brand/search"]["post"]["parameters"]["query"]["orderBy"]>;
type BrandQueryParams = paths["/v1/brand/search"]["post"]["parameters"]["query"];
type BrandRequestBody = paths["/v1/brand/search"]["post"]["requestBody"]["content"]["application/json"];
type BrandExtend = NonNullable<paths["/v1/brand/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: BrandExtend = [PathsV1BrandSearchPostParametersQueryExtend.BrandIdentifiers] as const;

const DEFAULT_COLUMNS = [
  brandTableDefaultColumns.columnName,
  brandTableDefaultColumns.columnTechnicalId,
  brandTableDefaultColumns.columnDatId,
  brandTableDefaultColumns.type,
  brandTableDefaultColumns.columnDeletedAt,
];

const BRAND_TABLE_CONFIG: PredefinedTableConfig<BrandSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1BrandSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1BrandSearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchBrands"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: true,
  } as const,
  sortingMapping: {
    name: ["name"],
    datId: ["datId"],
    technicalId: ["technicalId"],
    type: ["type"],
    deletedAt: ["deletedAt"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
  sortColumns: [
    { id: PathsV1BrandSearchPostParametersQueryOrderByField.Name, label: () => i18next.t("common:Name") },
    { id: PathsV1BrandSearchPostParametersQueryOrderByField.TechnicalId, label: () => i18next.t("common:TechnicalId") },
    { id: PathsV1BrandSearchPostParametersQueryOrderByField.DatId, label: () => i18next.t("masterdata:DATID") },
    { id: PathsV1BrandSearchPostParametersQueryOrderByField.Type, label: () => i18next.t("masterdata:Type") },
  ],
  timestampSortFields: [TimestampSortField.CreatedAt, TimestampSortField.UpdatedAt, TimestampSortField.DeletedAt],
} as const;

const BRAND_TABLE_QUERY_HOOKS: DataFetchHooks<Brand, BrandQueryParams, BrandRequestBody> = {
  useSearchQuery: useBrandSearchQuery,
  useDeleteMutation: useBrandDeleteMutation,
  useRestoreMutation: useBrandRestoreMutation,
} as const;

export interface BrandTableProps extends BasePredefinedTableProps<
  Brand,
  BrandSorting,
  BrandQueryParams,
  BrandRequestBody
> {
  extend?: BrandExtend;
  onMerge?: (brand: Brand) => void;
}

const BaseBrandTable = createBasePredefinedTable({
  config: BRAND_TABLE_CONFIG,
  queryHooks: BRAND_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function BrandTable(props: BrandTableProps) {
  const { t } = useTranslation(["common"]);
  const {
    name = "brand-table",
    extend = DEFAULT_EXTEND,
    showCreateButton = true,
    onMerge,
    body = { deletedAt: [DeletedStatus.ACTIVE, DeletedStatus.DELETED] },
    filters,
    filterValues,
    columns,
    ...restProps
  } = props;

  const defaultFilters = useMemo(
    () => filters ?? [new BrandDatIdFilter({ body }), new BrandTypeFilter(), new DeletedStatusFilter()],
    [body, filters],
  );
  const defaultFilterValues = useMemo(() => filterValues ?? { deletedAt: [DeletedStatus.ACTIVE] }, [filterValues]);

  const customRowActions = useCallback(
    (row: { original: Brand }) => {
      if (!onMerge || row.original.deletedAt) return [];

      return [
        {
          label: t("masterdata:Merge"),
          icon: <GitMerge className="h-4 w-4" />,
          variant: RowActionVariant.Warning,
          onClick: () => onMerge(row.original),
        },
      ];
    },
    [onMerge, t],
  );

  return (
    <BaseBrandTable
      searchPlaceholder={t("common:SearchBrands")}
      {...{
        name,
        extend,
        showCreateButton,
        body,
        columns,
        filters: defaultFilters,
        filterValues: defaultFilterValues,
        confirmActions: ["delete", "restore"],
        toastMessages: {
          deleteSuccess: t("BrandHasBeenDeleted"),
          deleteError: t("DeleteBrandError"),
          restoreSuccess: t("BrandHasBeenRestored"),
          restoreError: t("RestoreBrandError"),
        },
        confirmLabels: {
          delete: (row: Brand) => ({
            title: row.deletedAt ? t("DeleteBrandPermanently") : t("DeleteBrand"),
            text: row.deletedAt ? t("DeleteBrandPermanentlyDescription") : t("DeleteBrandDescription"),
            confirmButton: row.deletedAt ? t("YesDeletePermanently") : t("ram:YesDelete"),
            cancelButton: t("Cancel"),
          }),
          restore: {
            title: t("RestoreBrand"),
            text: t("RestoreBrandDescription"),
            confirmButton: t("YesRestore"),
            cancelButton: t("Cancel"),
          },
        },
        customRowActions,
        ...restProps,
      }}
    />
  );
}
